<template>
  <div class="formulario-modelo">
    <LoadingOverlay :visible="loading" message="Cargando configuración…" />

    <header class="fo-header">
      <div class="fo-header-top">
        <div>
          <h2 class="fo-titulo">Ficha de Opinión</h2>
          <p v-if="nombreVersion" class="ctx-modelo">{{ nombreVersion }}</p>
          <p v-if="instrumento" class="fo-subtitulo">{{ instrumento }}</p>
        </div>
        <button class="btn volver" @click="router.back()">Volver</button>
      </div>
    </header>

    <!-- Sin configuración: se crea aquí mismo (una sola por formulario) -->
    <div v-if="!loading && !configuracion" class="fo-vacio">

      <div class="fo-aviso">
        <p class="fo-aviso-titulo">Este formulario aún no ha sido aplicado</p>
        <p class="fo-aviso-cuerpo">
          El cuestionario <strong>{{ instrumentoNombre || 'de opinión' }}</strong> ya existe con sus preguntas
          configuradas. Para poder enviar el formulario a los encuestados, primero debe registrar
          su vigencia y título.
        </p>
        <ul class="fo-aviso-pasos">
          <li>Complete el título que verán los encuestados y el período de vigencia.</li>
          <li>Guarde para generar los formularios de Google.</li>
          <li>Publique y asigne destinatarios para comenzar a recibir respuestas.</li>
        </ul>
      </div>

      <div class="fo-crear">
        <div class="form-group">
          <label>Título que verán los encuestados</label>
          <input v-model="formNueva.titulo" type="text" class="form-control" placeholder="Encuesta de satisfacción 2026" />
        </div>
        <div class="form-group">
          <label>Instrucciones</label>
          <textarea v-model="formNueva.instrucciones" rows="2" class="form-control" placeholder="Responda según su experiencia…"></textarea>
        </div>
        <div class="fo-crear-fechas">
          <div class="form-group">
            <label>Vigencia desde</label>
            <input v-model="formNueva.vigencia_desde" type="date" class="form-control" />
          </div>
          <div class="form-group">
            <label>Vigencia hasta</label>
            <input v-model="formNueva.vigencia_hasta" type="date" class="form-control" />
          </div>
        </div>

        <button class="btn guardar" :disabled="creando || !formNueva.titulo.trim()" @click="crearConfiguracion">
          {{ creando ? 'Generando…' : 'Aplicar y generar formularios' }}
        </button>
      </div>
    </div>

    <!-- Vista principal con pestañas -->
    <template v-else-if="configuracion">
      <div style="margin-top: 16px;"></div>

      <!-- Estado, vigencia y selector de período -->
      <div class="estado-row">
        <div class="estado-info">
          <span :class="['estado-badge', `estado-${configuracion.estado}`]">
            {{ configuracion.estado }}
          </span>
          <span class="vigencia-texto">
            Vigencia: {{ formatearFecha(configuracion.vigencia_desde) }} → {{ formatearFecha(configuracion.vigencia_hasta) }}
          </span>

          <!-- En borrador los formularios de Google todavía no existen: hasta
               publicar no hay enlaces que enviar ni destinatarios que asignar. -->
          <button
            v-if="configuracion.estado === 'borrador'"
            class="btn guardar btn-sm"
            :disabled="publicando"
            @click="publicarConfiguracion"
          >
            {{ publicando ? 'Publicando…' : 'Publicar formularios' }}
          </button>
        </div>
        <div v-if="configuraciones.length > 1" class="config-selector">
          <label class="config-selector-label">Período:</label>
          <select v-model="configSeleccionadaId" class="config-select" @change="cargarConfigSeleccionada">
            <option v-for="c in configuraciones" :key="c.id_configuracion" :value="c.id_configuracion">
              {{ formatearFecha(c.vigencia_desde) }} → {{ formatearFecha(c.vigencia_hasta) }} ({{ c.estado }})
            </option>
          </select>
        </div>
      </div>

      <!-- Datos del formulario e indicador -->
      <div class="ficha-info">
        <div v-if="configuracion.titulo" class="fi-titulo">{{ configuracion.titulo }}</div>
        <!-- Un formulario cubre varios aspectos: se listan los indicadores que
             evalúan sus preguntas, no uno solo. -->
        <div class="fi-cubiertos">
          <div class="fi-cubiertos-header">
            <span class="fi-lbl">Indicadores que cubre este formulario</span>
            <button
              v-if="indicadoresCubiertos.length"
              type="button"
              class="fi-toggle"
              @click="indicadoresExpandidos = !indicadoresExpandidos"
            >
              {{ indicadoresExpandidos ? 'Ocultar' : `Mostrar (${indicadoresCubiertos.length})` }}
            </button>
          </div>

          <ul v-if="indicadoresCubiertos.length && indicadoresExpandidos" class="lista-cubiertos">
            <li v-for="ind in indicadoresCubiertos" :key="ind.id_indicador" class="item-cubierto">
              <span v-if="ind.ordinal" class="ic-ordinal">{{ ind.ordinal }}</span>
              <span class="ic-texto">
                <strong class="ic-componente">{{ ind.aspecto || '—' }}</strong>
                <span class="ic-enunciado">{{ ind.enunciado || '—' }}</span>
              </span>
              <span class="ic-preguntas">
                {{ ind.preguntas }} pregunta{{ ind.preguntas === 1 ? '' : 's' }}
              </span>
            </li>
          </ul>

          <p v-else-if="!indicadoresCubiertos.length" class="fi-vacio">
            Ninguna pregunta de este formulario tiene indicador asignado todavía.
            Vincúlalo desde la vista de indicadores.
          </p>
        </div>
        <div v-if="configuracion.instrucciones" class="fi-instrucciones">
          <span class="fi-lbl">Instrucciones</span>
          <p>{{ configuracion.instrucciones }}</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-btn', { 'tab-btn--activo': tabActiva === tab.id }]"
          @click="tabActiva = tab.id"
        >
          {{ tab.label }}
          <span v-if="tab.badge" class="tab-badge">{{ tab.badge }}</span>
        </button>
      </div>

      <div class="marco">

        <!-- ── TAB: Formularios ── -->
        <div v-if="tabActiva === 'formularios'" class="tab-content">
          <div v-if="!formularios.length" class="vacio">Sin formularios generados.</div>

          <div v-for="f in formularios" :key="f.id_formulario_google" class="form-card">
            <div class="form-card-head">
              <div class="form-card-grupo">
                <strong>{{ f.grupo_interes?.nombre ?? `Formulario ${f.id_formulario_google}` }}</strong>
                <span :class="['estado-badge', `estado-${f.estado}`]">{{ f.estado }}</span>
              </div>
              <div class="form-card-acciones">
                <template v-if="f.google_form_url">
                  <button class="btn btn-sm guardar" @click="copiarUrl(f.google_form_url!)">Copiar link</button>
                  <a :href="f.google_form_url" target="_blank" rel="noopener" class="btn btn-sm guardar">Abrir</a>
                </template>
                <span v-else class="muted">URL pendiente</span>
                <button
                  v-if="configuracion!.estado === 'publicada'"
                  class="btn btn-sm salir"
                  :disabled="loadingAccion"
                  @click="cerrarConfiguracion"
                >
                  Cerrar formularios
                </button>
              </div>
            </div>

            <div v-if="f.preguntas_snapshot?.length" class="snapshot-lista">
              <div v-for="(p, i) in f.preguntas_snapshot" :key="p.id_pregunta" class="snapshot-item">
                <div class="snap-header">
                  <span class="snap-n">{{ i + 1 }}</span>
                  <span class="snap-enunciado">{{ p.enunciado }}</span>
                </div>
                <div class="snap-respuesta-area">
                  <span class="snap-respuesta-placeholder">Espacio para respuesta</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── TAB: Destinatarios ── -->
        <div v-if="tabActiva === 'destinatarios'" class="tab-content">
          <div v-if="configuracion!.estado === 'cerrada'" class="aviso-readonly">
            Formularios cerrados — solo lectura.
          </div>

          <!-- Sub-pestañas por grupo de interés -->
          <div v-if="formularios.length" class="dest-tabs">
            <button
              v-for="f in formularios"
              :key="f.id_formulario_google"
              :class="['dest-tab-btn', { 'dest-tab-btn--activo': grupoActivo === f.id_formulario_google }]"
              @click="grupoActivo = f.id_formulario_google"
            >
              {{ f.grupo_interes?.nombre ?? `Grupo ${f.id_formulario_google}` }}
              <span v-if="f.destinatarios?.length" class="tab-badge">{{ f.destinatarios.length }}</span>
            </button>
          </div>

          <template v-for="f in formularios" :key="f.id_formulario_google">
            <div v-if="grupoActivo === f.id_formulario_google" class="dest-grupo">
              <table v-if="f.destinatarios?.length" class="dest-tabla">
                <thead>
                  <tr><th>Email</th><th>Estado envío</th><th>Enviado</th></tr>
                </thead>
                <tbody>
                  <tr v-for="d in f.destinatarios" :key="d.email">
                    <td>{{ d.email }}</td>
                    <td><span :class="['estado-badge', `estado-envio-${d.estado_envio}`]">{{ d.estado_envio }}</span></td>
                    <td>{{ formatearFecha(d.enviado_at) }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="muted">Sin destinatarios asignados.</p>

              <ContactosPreviosSelector
                v-if="configuracion!.estado === 'publicada'"
                :idGrupoInteres="f.id_grupo_interes ?? null"
                :idPrograma="idPrograma"
                :idEjecucion="idEjecucionActual"
                @agregar="agregarContactosPrevios(f.id_formulario_google, $event)"
              />

              <div v-if="configuracion!.estado === 'publicada'" class="dest-input-wrap">
                <div class="dest-label-row">
                  <label class="dest-label">Agregar nuevos correos (uno por línea)</label>
                  <button type="button" class="btn btn-sm guardar" @click="abrirExcel(f.id_formulario_google)">
                    Cargar desde Excel
                  </button>
                </div>
                <textarea
                  v-model="correosInput[f.id_formulario_google]"
                  class="dest-textarea"
                  placeholder="usuario@ejemplo.com&#10;otro@ejemplo.com"
                  rows="4"
                />

                <!-- Vigencia de la afiliación al área: pasada esa fecha el
                     correo deja de ofrecerse en el banco. Una carrera dura
                     unos 5 años; sin fecha, el correo no caduca nunca. -->
                <div class="vigencia-row">
                  <label class="vigencia-campo">
                    <span>Afiliado desde</span>
                    <input type="date" v-model="vigenciaDesde[f.id_formulario_google]" />
                  </label>
                  <label class="vigencia-campo">
                    <span>Vigente hasta</span>
                    <input type="date" v-model="vigenciaHasta[f.id_formulario_google]" />
                  </label>
                  <button type="button" class="cp-link" @click="vigenciaCincoAnios(f.id_formulario_google)">
                    5 años
                  </button>
                </div>
                <p class="vigencia-ayuda">
                  Pasada la fecha de vigencia el correo desaparece del banco y no se vuelve
                  a ofrecer. Si lo dejas en blanco, no caduca.
                </p>
              </div>

              <div v-if="configuracion!.estado === 'publicada'" class="tab-footer">
                <button
                  class="btn guardar"
                  :disabled="loadingAccion || !correosInput[f.id_formulario_google]?.trim()"
                  @click="asignarDestinatariosGrupo(f.id_formulario_google)"
                >
                  {{ loadingAccion ? 'Enviando…' : 'Enviar a este grupo' }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <!-- ── TAB: Respuestas ── -->
        <div v-if="tabActiva === 'respuestas'" class="tab-content">
          <div class="respuestas-head">
            <span class="total-badge">{{ totalRespuestas }} respuesta(s)</span>
            <div class="vista-toggle">
              <button
                :class="['vista-btn', { 'vista-btn--activo': vistaRespuestas === 'pregunta' }]"
                @click="vistaRespuestas = 'pregunta'"
              >Por pregunta</button>
              <button
                :class="['vista-btn', { 'vista-btn--activo': vistaRespuestas === 'persona' }]"
                @click="vistaRespuestas = 'persona'"
              >Por persona</button>
            </div>
            <span
              v-if="participacion"
              class="participacion-badge"
              :class="{ 'participacion-badge--vacio': participacion.porcentaje === null }"
              :title="tituloParticipacion"
            >
              <template v-if="participacion.porcentaje !== null">
                Participación {{ participacion.porcentaje }}%
                <small>({{ participacion.respondieron }}/{{ participacion.correos_enviados }} correos)</small>
              </template>
              <template v-else>Participación — <small>(sin correos enviados)</small></template>
            </span>
            <button class="btn guardar btn-sm" :disabled="loadingAccion" @click="sincronizar">
              {{ loadingAccion ? 'Sincronizando…' : 'Sincronizar respuestas' }}
            </button>
          </div>

          <div v-if="!respuestas.length" class="vacio">
            Sin respuestas registradas. Sincroniza para actualizar.
          </div>

          <!-- Sub-pestañas por grupo de interés -->
          <div v-if="respuestas.length" class="dest-tabs">
            <button
              v-for="rf in respuestas"
              :key="rf.id_formulario_google"
              :class="['dest-tab-btn', { 'dest-tab-btn--activo': grupoActivoRespuestas === rf.id_formulario_google }]"
              @click="grupoActivoRespuestas = rf.id_formulario_google; personaFiltrada = null"
            >
              {{ rf.grupo_interes?.nombre ?? `Grupo ${rf.id_formulario_google}` }}
              <span v-if="rf.respuestas.length" class="tab-badge">{{ rf.respuestas.length }}</span>
            </button>
          </div>

          <template v-for="rf in respuestas" :key="rf.id_formulario_google">
            <div v-if="grupoActivoRespuestas === rf.id_formulario_google" class="resp-grupo">

              <div v-if="rf.respondientes?.length" class="resp-personas">
                <span class="resp-personas-label">Respondieron ({{ rf.respondientes.length }}):</span>
                <span
                  v-for="p in rf.respondientes"
                  :key="p.google_response_id"
                  class="resp-persona-chip"
                  :title="p.email || ''"
                >
                  {{ p.nombre || p.email || 'Anónimo' }}
                </span>
              </div>

              <!-- Vista por pregunta -->
              <template v-if="vistaRespuestas === 'pregunta'">
                <div v-for="(grupo, idPregunta) in agruparPorPregunta(rf.respuestas)" :key="idPregunta" class="resp-pregunta">
                  <p class="resp-enunciado">{{ grupo.enunciado }}</p>

                  <div v-if="grupo.tieneValores" class="resp-promedio">
                    <span class="prom-label">Promedio</span>
                    <span class="prom-valor">{{ grupo.promedio.toFixed(2) }}</span>
                    <div class="prom-bar-wrap">
                      <div class="prom-bar" :style="{ width: `${(grupo.promedio / 5) * 100}%` }"></div>
                    </div>
                  </div>

                  <ul v-if="grupo.textos.length" class="resp-textos">
                    <li v-for="(t, i) in grupo.textos" :key="i">
                      <span class="resp-texto-autor">{{ t.nombre }}:</span> {{ t.texto }}
                    </li>
                  </ul>
                </div>
              </template>

              <!-- Vista por persona -->
              <template v-else>
                <div v-if="rf.respondientes?.length" class="persona-filtro">
                  <label class="persona-filtro-label">Filtrar por persona:</label>
                  <select v-model="personaFiltrada" class="persona-filtro-select">
                    <option :value="null">— Todos —</option>
                    <option v-for="r in rf.respondientes" :key="r.google_response_id" :value="r.google_response_id">
                      {{ r.nombre || r.email || 'Anónimo' }}
                    </option>
                  </select>
                </div>
                <p v-if="!rf.respondientes?.length" class="vacio">Aún no hay respuestas.</p>
                <div
                  v-for="p in (personaFiltrada ? rf.respondientes?.filter(r => r.google_response_id === personaFiltrada) : rf.respondientes)"
                  :key="p.google_response_id"
                  class="persona-card"
                >
                  <div class="persona-head">
                    <strong class="persona-nombre">{{ p.nombre || p.email || 'Anónimo' }}</strong>
                    <span v-if="p.nombre && p.email" class="persona-email">{{ p.email }}</span>
                    <span v-if="rf.grupo_interes?.nombre" class="persona-rol">→ {{ rf.grupo_interes.nombre }}</span>
                    <span v-if="p.respondido_at" class="persona-fecha">{{ p.respondido_at.substring(0, 10) }}</span>
                  </div>
                  <ul class="persona-respuestas">
                    <li v-for="r in respuestasDe(rf, p.google_response_id)" :key="r.id_respuesta">
                      <span class="pr-enunciado">{{ r.enunciado }}</span>
                      <span class="pr-valor">{{ r.texto ?? r.valor ?? '—' }}</span>
                    </li>
                  </ul>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- ── TAB: Calificación ── -->
        <div v-if="tabActiva === 'calificacion'" class="tab-content">
          <!-- Una ficha cubre varios indicadores y cada uno tiene SU nota: la
               calificación se lista por indicador, no como un valor global. -->
          <div v-if="indicadoresCubiertos.length" class="cal-existente">
            <div class="cal-existente-head">Calificación por indicador</div>

            <ul class="cal-lista">
              <li v-for="ind in indicadoresCubiertos" :key="ind.id_indicador" class="cal-item-ind">
                <div class="cal-ind-cab">
                  <span v-if="ind.ordinal" class="ic-ordinal">{{ ind.ordinal }}</span>
                  <span class="ic-texto">
                    <strong>{{ ind.enunciado || '—' }}</strong>
                    <span v-if="ind.aspecto" class="ic-aspecto">{{ ind.aspecto }}</span>
                  </span>
                </div>

                <div v-if="ind.calificacion?.opinion != null" class="cal-grid">
                  <div class="cal-item">
                    <span class="cal-label">Opinión</span>
                    <span :class="['cal-valor', 'cal-nota', claseSemaforoDesdeNivel(ind.calificacion.nivel)]">
                      {{ ind.calificacion.opinion.toFixed(2) }}
                    </span>
                  </div>
                  <div v-if="ind.calificacion.final != null" class="cal-item">
                    <span class="cal-label">Final ponderada</span>
                    <span :class="['cal-valor', 'cal-nota', claseSemaforoDesdeNivel(ind.calificacion.nivel)]">
                      {{ ind.calificacion.final.toFixed(2) }}
                    </span>
                  </div>
                  <div v-if="ind.calificacion.nivel" class="cal-item">
                    <span class="cal-label">Nivel</span>
                    <span class="cal-valor">{{ ind.calificacion.nivel }}</span>
                  </div>
                  <div class="cal-item">
                    <span class="cal-label">Preguntas calificadas</span>
                    <span class="cal-valor cal-valor--tenue">
                      {{ ind.calificacion.preguntas_calificadas ?? 0 }} de {{ ind.preguntas }}
                    </span>
                  </div>
                </div>

                <p v-else class="cal-sin-nota">Sin calificar.</p>
              </li>
            </ul>
          </div>

          <!-- Formulario que califica ENTERO: una sola nota para el indicador,
               no una por pregunta. -->
          <form v-if="esFormularioCompleto" class="cal-form" @submit.prevent="calificarCompleto">
            <h4>{{ hayCalificacion ? 'Corregir la calificación' : 'Registrar calificación' }}</h4>
            <p class="cal-aviso">
              Este formulario califica como <strong>una sola unidad</strong>: la nota que
              registres es la del indicador, leyendo las respuestas tal como llegaron.
            </p>

            <div class="form-group">
              <label>Nota ({{ escalaMax > 5 ? `1 – ${escalaMax}` : `0 – ${escalaMax}` }})</label>
              <input
                type="number"
                v-model.number="formCal.valor"
                :min="escalaMax > 5 ? 1 : 0"
                :max="escalaMax"
                step="0.01"
                required
                class="form-control input-nota"
              />
            </div>

            <div class="form-group">
              <label>Observación</label>
              <textarea v-model="formCal.observacion" class="form-control" rows="3" placeholder="Análisis de lo que se recogió…" />
            </div>

            <div class="form-group">
              <label>Comparación con períodos anteriores</label>
              <textarea v-model="formCal.comparacion" class="form-control" rows="2" placeholder="Comparación…" />
            </div>

            <div class="tab-footer">
              <button type="submit" class="btn guardar" :disabled="loadingAccion">
                {{ loadingAccion ? 'Guardando…' : 'Guardar calificación' }}
              </button>
            </div>
          </form>

          <!-- Encuesta: la calificación es POR PREGUNTA, cada una apunta a su
               indicador y la nota de éste es el promedio de las suyas. -->
          <div v-else class="cal-form">
            <h4>{{ hayCalificacion ? 'Continuar o corregir la calificación' : 'Registrar calificación' }}</h4>
            <p class="cal-aviso">
              La calificación de opinión se hace <strong>pregunta por pregunta</strong>,
              mostrando aspecto, indicador, pregunta y respuestas junto a la rúbrica.
              Escala de calificación: <strong>{{ escalaMax > 5 ? `1 – ${escalaMax}` : `0 – ${escalaMax}` }}</strong>.
            </p>
            <button type="button" class="btn guardar" :disabled="!configuracion" @click="irACalificarPorPregunta">
              Calificar por pregunta
            </button>
          </div>

        </div>

      </div><!-- /.marco -->
    </template>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />

    <input
      ref="fileInputRef"
      type="file"
      accept=".xlsx,.xls,.csv"
      style="display:none"
      @change="onArchivoExcel"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Ficha de evidencia de opinión, alimentada por formularios.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/ficha-opinion` (FichaOpinion) — permiso `evidencias.subir`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { read, utils } from 'xlsx'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import ContactosPreviosSelector from '@/components/ContactosPreviosSelector.vue'
import { useGestionConfiguracionInstrumento } from '@/store/GestionConfiguracionInstrumento'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useModelo } from '@/store/GestionModelo'
import useAutenticar from '@/store/IngresoAutenticar'
import { primerError } from '@/services/http'
import type ConfiguracionInstrumento from '@/interfaces/ConfiguracionInstrumento'
import type { FormularioGoogle, IndicadorCubierto, RespuestasFormulario, RespuestaIndividual, Participacion,} from '@/interfaces/ConfiguracionInstrumento'

const route = useRoute()
const router = useRouter()
const configStore = useGestionConfiguracionInstrumento()
const instrumentoStore = useGestionInstrumento()
const versionesStore = useGestionVersiones()
const modeloStore = useModelo()
const authStore = useAutenticar()
const escalaMax = ref<number>(5)
const nombreVersion = ref('')
const loading = ref(false)
const loadingAccion = ref(false)
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')
const instrumento = ref('')
const instrumentoNombre = ref('')
const configuraciones = ref<ConfiguracionInstrumento[]>([])
const configuracion = ref<ConfiguracionInstrumento | null>(null)
const configSeleccionadaId = ref<number | null>(null)
const respuestas = ref<RespuestasFormulario[]>([])
const participacion = ref<Participacion | null>(null)
const tabActiva = ref<'formularios' | 'destinatarios' | 'respuestas' | 'calificacion'>('formularios')
const vistaRespuestas = ref<'pregunta' | 'persona'>('pregunta')
const grupoActivo = ref<number | null>(null)
const grupoActivoRespuestas = ref<number | null>(null)
const personaFiltrada = ref<string | null>(null)


const publicando = ref(false)
const indicadoresExpandidos = ref(false)

function claseSemaforoDesdeNivel(nivel: string | null | undefined): string {
  if (!nivel) return ''
  const n = nivel.toLowerCase()
  if (n.includes('plenamente'))  return 'nivel-excelente'
  if (n.includes('alto grado'))  return 'nivel-alto'
  if (n.includes('aceptabl'))    return 'nivel-medio'
  return 'nivel-bajo'
}

/** Área bajo la que se ofrecen los correos ya usados por grupo de interés. */
const idPrograma = computed<number | null>(() => configuracion.value?.id_programa ?? null)

const idEjecucionActual = computed<number | null>(() => {
  const raw = Array.isArray(route.query.id_ejecucion) ? route.query.id_ejecucion[0] : route.query.id_ejecucion
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
})

function agregarContactosPrevios(idForm: number, elegidos: Array<{ email: string }>) {
  const formulario = formularios.value.find(f => f.id_formulario_google === idForm)
  const yaAsignados = new Set((formulario?.destinatarios ?? []).map((d: any) => d.email.toLowerCase()))
  const yaEscritos = new Set(
    (correosInput.value[idForm] ?? '').split('\n').map(e => e.trim().toLowerCase()).filter(Boolean)
  )

  const nuevos = elegidos
    .map(c => c.email)
    .filter(e => !yaAsignados.has(e.toLowerCase()) && !yaEscritos.has(e.toLowerCase()))

  if (nuevos.length === 0) {
    mostrarMensaje('Esos correos ya estaban en la lista.', 'error')
    return
  }

  const actual = (correosInput.value[idForm] ?? '').trim()
  correosInput.value[idForm] = actual ? `${actual}\n${nuevos.join('\n')}` : nuevos.join('\n')
  mostrarMensaje(`${nuevos.length} correo(s) agregados del banco.`, 'positivo')
}

/**
 * Publica la configuración: es lo que crea los formularios de Google y sus
 * enlaces. Sin este paso no hay nada que enviar ni a quién asignárselo.
 */
async function publicarConfiguracion() {
  if (!configuracion.value) return
  publicando.value = true
  try {
    const result = await configStore.publicar(configuracion.value.id_configuracion)
    if (!result?.ok) {
      const primero = Object.values(result?.errors ?? {}).flat()[0]
      mostrarMensaje(primero ?? 'No se pudieron publicar los formularios.', 'error')
      return
    }
    await cargarConfiguracion(configuracion.value.id_configuracion)
    mostrarMensaje('Formularios publicados. Ya puede asignar destinatarios.', 'positivo')
  } finally {
    publicando.value = false
  }
}

// Alta de la configuración cuando el formulario aún no la tiene
const formNueva = reactive({ titulo: '', instrucciones: '', vigencia_desde: '', vigencia_hasta: '' })
const creando = ref(false)

/**
 * Crea la configuración del formulario. No se envía id_indicador: es una por
 * formulario y los indicadores que cubre salen de sus preguntas.
 */
async function crearConfiguracion() {
  const id = idInstrumento()
  if (!id || !formNueva.titulo.trim()) return

  creando.value = true
  try {
    const result = await configStore.crear({
      id_instrumento: id,
      titulo: formNueva.titulo.trim(),
      instrucciones: formNueva.instrucciones.trim() || undefined,
      vigencia_desde: formNueva.vigencia_desde || undefined,
      vigencia_hasta: formNueva.vigencia_hasta || undefined
    })

    if (!result?.ok || !result.data) {
      const primero = Object.values(result?.errors ?? {}).flat()[0]
      mostrarMensaje(primero ?? 'No se pudo configurar el formulario.', 'error')
      return
    }

    configuraciones.value = [result.data]
    configSeleccionadaId.value = result.data.id_configuracion
    await cargarConfiguracion(result.data.id_configuracion)
    mostrarMensaje('Formulario configurado.', 'positivo')
  } finally {
    creando.value = false
  }
}

const indicadoresCubiertos = computed<IndicadorCubierto[]>(
  () => configuracion.value?.indicadores_cubiertos ?? []
)

const hayCalificacion = computed(() =>
  indicadoresCubiertos.value.some(ind => ind.calificacion?.opinion != null)
)

/**
 * El formulario evalúa un solo indicador y se califica con una nota única, en
 * vez de pregunta por pregunta.
 */
const esFormularioCompleto = computed(
  () => configuracion.value?.modalidad === 'formulario_completo'
)

const formCal = reactive({ valor: 0, observacion: '', comparacion: '' })

/** Nota única del formulario completo (carril RF-07 a nivel de instrumento). */
async function calificarCompleto() {
  if (!configuracion.value) return
  loadingAccion.value = true
  try {
    const idEjecucion = Number(
      Array.isArray(route.query.id_ejecucion) ? route.query.id_ejecucion[0] : route.query.id_ejecucion
    )
    const result = await configStore.calificar(configuracion.value.id_configuracion, {
      valor: formCal.valor,
      observacion: formCal.observacion || undefined,
      comparacion: formCal.comparacion || undefined,
      ...(Number.isFinite(idEjecucion) && idEjecucion > 0 ? { id_ejecucion: idEjecucion } : {})
    })

    if (result.ok) {
      await cargarConfiguracion(configuracion.value.id_configuracion)
      mostrarMensaje('Calificación guardada correctamente.', 'positivo')
    } else {
      mostrarMensaje(primerError(result, 'Error al guardar la calificación.'), 'error')
    }
  } finally {
    loadingAccion.value = false
  }
}

const correosInput = ref<Record<number, string>>({})
// Vigencia que se aplicará a los correos que se carguen en este envío.
const vigenciaDesde = ref<Record<number, string>>({})
const vigenciaHasta = ref<Record<number, string>>({})
const fileInputRef = ref<HTMLInputElement | null>(null)
const formularioExcelActivo = ref<number | null>(null)

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

function formatearFecha(fecha: string | null | undefined): string {
  if (!fecha) return '—'
  const d = new Date(fecha)
  if (isNaN(d.getTime())) return fecha
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
}

const formularios = computed<FormularioGoogle[]>(() => configuracion.value?.formularios ?? [])

const totalRespuestas = computed(() =>
  respuestas.value.reduce((acc, rf) => acc + rf.respuestas.length, 0)
)

// Desglose por grupo para el tooltip del badge de participación.
const tituloParticipacion = computed(() => {
  const p = participacion.value
  if (!p) return ''
  return p.por_formulario
    .map(f => {
      const pct = f.porcentaje === null ? '—' : `${f.porcentaje}%`
      return `${f.grupo_interes ?? 'Grupo'}: ${f.respondieron}/${f.correos_enviados} (${pct})`
    })
    .join('\n')
})

const tabs = computed(() => [
  { id: 'formularios' as const, label: 'Formularios' },
  { id: 'destinatarios' as const, label: 'Destinatarios' },
  { id: 'respuestas' as const, label: 'Respuestas', badge: totalRespuestas.value || undefined },
  { id: 'calificacion' as const, label: 'Calificación' },
])

function idInstrumento(): number | null {
  const raw = route.query.id_instrumento ?? route.params.id_instrumento ?? route.params.id
  const val = Array.isArray(raw) ? raw[0] : raw
  const n = Number(val)
  return Number.isFinite(n) && n > 0 ? n : null
}

onMounted(async () => {
  const id = idInstrumento()
  if (!id) {
    mostrarMensaje('No se encontró id_instrumento en la URL.', 'error')
    return
  }

  loading.value = true
  try {
    const inst = await instrumentoStore.getInstrumentoById(id)
    instrumento.value = inst?.herramienta ?? ''
    instrumentoNombre.value = (inst as any)?.descripcion || inst?.herramienta || ''

    // Se propone como título para no pedir dos veces el mismo nombre.
    if (!formNueva.titulo) formNueva.titulo = instrumentoNombre.value

    const rawVersion = route.query.id_version
    const idVersion = Number(Array.isArray(rawVersion) ? rawVersion[0] : rawVersion)
    if (Number.isFinite(idVersion) && idVersion > 0) {
      const version = await versionesStore.getVersionById(idVersion)
      nombreVersion.value = version?.nombre ?? ''
      if (version?.id_modelo) {
        const modelo = await modeloStore.getModeloById(version.id_modelo)
        if (modelo?.escala_max) escalaMax.value = modelo.escala_max
      }
    }

    await configStore.listar({ id_instrumento: id })

    // La configuración es UNA POR FORMULARIO, así que basta con acotar al
    // instrumento: no se filtra por indicador porque las nuevas nacen sin él
    // (los indicadores que cubre salen de las preguntas).
    configuraciones.value = [...configStore.configuraciones].filter(c => c.id_instrumento === id)

    if (configuraciones.value.length > 0) {
      const ultima = configuraciones.value[configuraciones.value.length - 1]
      configSeleccionadaId.value = ultima.id_configuracion
      await cargarConfiguracion(ultima.id_configuracion)
    }
  } finally {
    loading.value = false
  }
})

async function cargarConfiguracion(idConfig: number) {
  // Cada indicador cubierto llega con SU calificación: se acota a la ejecución
  // en curso y al calificador para no mostrar la nota de otro proceso.
  const params: { id_ejecucion?: number; id_usuario?: number } = {}
  const idEjecucion = Number(
    Array.isArray(route.query.id_ejecucion) ? route.query.id_ejecucion[0] : route.query.id_ejecucion
  )
  if (Number.isFinite(idEjecucion) && idEjecucion > 0) params.id_ejecucion = idEjecucion
  const idUsuario = authStore.usuarioActual?.id
  if (idUsuario) params.id_usuario = idUsuario

  const result = await configStore.obtener(idConfig, params)
  if (result.ok && result.data) {
    configuracion.value = result.data
    participacion.value = result.data.participacion ?? null
    inicializarCorreosInput()
  }
}

async function cargarConfigSeleccionada() {
  if (!configSeleccionadaId.value) return
  loading.value = true
  await cargarConfiguracion(configSeleccionadaId.value)
  respuestas.value = []
  loading.value = false
}

/** Atajo: el caso típico es una carrera de 5 años desde hoy. */
function vigenciaCincoAnios(idForm: number) {
  const hoy = new Date()
  const fin = new Date(hoy)
  fin.setFullYear(fin.getFullYear() + 5)
  vigenciaDesde.value[idForm] = hoy.toISOString().slice(0, 10)
  vigenciaHasta.value[idForm] = fin.toISOString().slice(0, 10)
}

function inicializarCorreosInput() {
  correosInput.value = {}
  vigenciaDesde.value = {}
  vigenciaHasta.value = {}
  for (const f of formularios.value) {
    correosInput.value[f.id_formulario_google] = ''
    vigenciaDesde.value[f.id_formulario_google] = ''
    vigenciaHasta.value[f.id_formulario_google] = ''
  }
  if (!grupoActivo.value && formularios.value.length) {
    grupoActivo.value = formularios.value[0].id_formulario_google
  }
}

async function asignarDestinatariosGrupo(idFormulario: number) {
  if (!configuracion.value) return
  const texto = correosInput.value[idFormulario] ?? ''
  const emails = texto.split('\n').map(e => e.trim()).filter(e => e.includes('@'))
  if (!emails.length) {
    mostrarMensaje('No hay correos nuevos para enviar a este grupo.', 'error')
    return
  }
  loadingAccion.value = true
  // La vigencia viaja con cada correo: el banco la guarda al archivarlos y
  // deja de ofrecerlos cuando caduca.
  const desde = vigenciaDesde.value[idFormulario] || undefined
  const hasta = vigenciaHasta.value[idFormulario] || undefined
  const payload = {
    [idFormulario]: emails.map(email => ({
      email,
      ...(desde ? { vigencia_desde: desde } : {}),
      ...(hasta ? { vigencia_hasta: hasta } : {})
    }))
  }
  const result = await configStore.asignarDestinatarios(
    configuracion.value.id_configuracion,
    payload,
    idEjecucionActual.value
  )
  if (result.ok) {
    const banco = result.data?.contactos_registrados ?? 0
    mostrarMensaje(
      banco > 0
        ? `Enviado. ${banco} correo(s) quedaron en el banco.`
        : 'Formulario enviado a los destinatarios.',
      'positivo'
    )
    correosInput.value[idFormulario] = ''
    await cargarConfiguracion(configuracion.value.id_configuracion)
  } else {
    mostrarMensaje(primerError(result, 'Error al asignar destinatarios.'), 'error')
  }
  loadingAccion.value = false
}

function abrirExcel(idFormulario: number) {
  formularioExcelActivo.value = idFormulario
  fileInputRef.value?.click()
}

function onArchivoExcel(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || formularioExcelActivo.value == null) return

  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target!.result as ArrayBuffer)
      const workbook = read(data, { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = utils.sheet_to_json<any[]>(sheet, { header: 1 })

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const encontrados: string[] = []
      for (const row of rows) {
        for (const cell of row) {
          const val = String(cell ?? '').trim()
          if (emailRegex.test(val)) encontrados.push(val)
        }
      }

      const idForm = formularioExcelActivo.value!
      const formulario = formularios.value.find(f => f.id_formulario_google === idForm)
      const yaAsignados = new Set((formulario?.destinatarios ?? []).map((d: any) => d.email.toLowerCase()))
      const yaEnTextarea = new Set(
        (correosInput.value[idForm] ?? '').split('\n').map(e => e.trim().toLowerCase()).filter(Boolean)
      )

      const nuevos = [...new Set(encontrados)].filter(
        e => !yaAsignados.has(e.toLowerCase()) && !yaEnTextarea.has(e.toLowerCase())
      )

      if (nuevos.length === 0) {
        mostrarMensaje('No se encontraron correos nuevos en el archivo.', 'error')
      } else {
        const actual = (correosInput.value[idForm] ?? '').trim()
        correosInput.value[idForm] = actual ? `${actual}\n${nuevos.join('\n')}` : nuevos.join('\n')
        mostrarMensaje(`${nuevos.length} correo(s) cargados desde el archivo.`, 'positivo')
      }
    } catch {
      mostrarMensaje('No se pudo leer el archivo. Verifique que sea un Excel o CSV válido.', 'error')
    } finally {
      input.value = ''
    }
  }
  reader.readAsArrayBuffer(file)
}

async function cerrarConfiguracion() {
  if (!configuracion.value) return
  loadingAccion.value = true
  const result = await configStore.cerrar(configuracion.value.id_configuracion)
  if (result.ok && result.data) {
    configuracion.value = result.data
    mostrarMensaje('Formularios cerrados correctamente.', 'positivo')
  } else {
    mostrarMensaje(primerError(result, 'Error al cerrar los formularios.'), 'error')
  }
  loadingAccion.value = false
}

async function sincronizar() {
  if (!configuracion.value) return
  loadingAccion.value = true
  const result = await configStore.sincronizar(configuracion.value.id_configuracion)
  if (result.ok) {
    // El % de participación se refresca con lo que devuelve la sincronización.
    if (result.data?.participacion) participacion.value = result.data.participacion
    mostrarMensaje(`Sincronización completada. Respuestas nuevas: ${result.data?.respuestas_nuevas ?? 0}`, 'positivo')
    await configStore.cargarRespuestas(configuracion.value.id_configuracion)
    respuestas.value = [...configStore.respuestas]
    if (!grupoActivoRespuestas.value && respuestas.value.length) {
      grupoActivoRespuestas.value = respuestas.value[0].id_formulario_google
    }
  } else {
    mostrarMensaje(primerError(result, 'Error al sincronizar.'), 'error')
  }
  loadingAccion.value = false
}

/**
 * Lleva a la calificación por pregunta. La ejecución se propaga si viene en la
 * URL: sin ella el par pregunta-respuesta se guarda sin ámbito y no queda
 * disponible para reutilizar.
 */
function irACalificarPorPregunta() {
  if (!configuracion.value?.id_configuracion) return
  router.push({
    name: 'CalificarInstrumentoOpinion',
    params: { id_configuracion: configuracion.value.id_configuracion },
    query: {
      ...(route.query.id_ejecucion ? { id_ejecucion: route.query.id_ejecucion } : {}),
      escala_max: escalaMax.value
    }
  })
}

function copiarUrl(url: string) {
  navigator.clipboard.writeText(url).then(() => {
    mostrarMensaje('Link copiado al portapapeles.', 'positivo')
  })
}

interface TextoRespuesta {
  texto: string
  nombre: string
}

interface GrupoRespuesta {
  enunciado: string
  tieneValores: boolean
  promedio: number
  textos: TextoRespuesta[]
}

function respuestasDe(rf: RespuestasFormulario, responseId: string): RespuestaIndividual[] {
  return rf.respuestas.filter(r => r.google_response_id === responseId)
}

function agruparPorPregunta(lista: RespuestaIndividual[]): Record<number, GrupoRespuesta> {
  const mapa: Record<number, GrupoRespuesta> = {}
  for (const r of lista) {
    if (!mapa[r.id_pregunta]) {
      mapa[r.id_pregunta] = { enunciado: r.enunciado, tieneValores: false, promedio: 0, textos: [] }
    }
    const g = mapa[r.id_pregunta]
    if (r.valor !== null) {
      g.tieneValores = true
      g.promedio = g.promedio + r.valor
    }
    if (r.texto) g.textos.push({ texto: r.texto, nombre: r.nombre_respondiente || r.email_respondiente || 'Anónimo' })
  }
  const conteo: Record<number, number> = {}
  for (const r of lista) {
    if (r.valor !== null) conteo[r.id_pregunta] = (conteo[r.id_pregunta] ?? 0) + 1
  }
  for (const [id, g] of Object.entries(mapa)) {
    if (g.tieneValores && conteo[Number(id)]) {
      g.promedio = g.promedio / conteo[Number(id)]
    }
  }
  return mapa
}
</script>

<style scoped>
/* ── Contenedor — igual que AgregarIndicadoresView ── */
.formulario-modelo {
  background-color: white;
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  width: 95%;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}

/* ── Header azul ── */
.fo-header {
  background: #0e69a0;
  color: #fff;
  padding: 20px 28px 16px;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  margin: calc(-1 * var(--space-lg)) calc(-1 * var(--space-lg)) 0;
}
.fo-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.fo-titulo { margin: 0 0 4px; font-size: 1.25rem; font-weight: 700; color: #fff; }
.ctx-modelo { display: inline-block; margin: 4px 0 6px; padding: 3px 10px; background: rgba(255,255,255,0.15); border-left: 3px solid #fff; border-radius: 3px; font-size: 0.82rem; font-weight: 700; color: #fff; letter-spacing: 0.01em; }
.fo-subtitulo { margin: 0; font-size: 0.9rem; color: #cfe0ec; }

/* ── Estado y selector de período ── */
.estado-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.estado-info { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.vigencia-texto { font-size: 0.85rem; color: #5b6b7a; }

/* Datos del formulario e indicador */
.ficha-info {
  background: #f0f7ff;
  border: 1px solid #bee3f8;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fi-titulo { font-size: 1.02rem; font-weight: 700; color: #0c447c; }
.fi-grid { display: flex; gap: 28px; flex-wrap: wrap; }

.fi-cubiertos { display: flex; flex-direction: column; gap: 8px; }

.fi-cubiertos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.fi-toggle {
  background: none;
  border: 1px solid #b8d0e8;
  border-radius: 5px;
  padding: 2px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e69a0;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
}
.fi-toggle:hover { background: #e8f2fa; }

.ic-componente { font-size: 0.88rem; color: #1e2a35; }
.ic-enunciado  { font-size: 0.78rem; color: #5b6b7a; }

.fo-crear {
  margin-top: 18px;
  text-align: left;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fo-crear-fechas { display: flex; gap: 12px; }
.fo-crear-fechas .form-group { flex: 1; }


.lista-cubiertos { list-style: none; margin: 0; padding: 0; }

.item-cubierto {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;
  background: #fff;
}

.ic-ordinal {
  flex-shrink: 0;
  background: #1f4e79;
  color: #fff;
  border-radius: 4px;
  padding: 1px 7px;
  font-size: 0.74rem;
  font-weight: 700;
}

.ic-texto { display: flex; flex-direction: column; gap: 2px; flex: 1; }

.ic-preguntas {
  flex-shrink: 0;
  font-size: 0.74rem;
  color: #0e69a0;
  font-weight: 600;
  white-space: nowrap;
}

.fi-vacio { margin: 0; font-size: 0.82rem; color: #888; }
.fi-item { display: flex; flex-direction: column; gap: 2px; }
.fi-lbl {
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; color: #5b6b7a;
}
.fi-val { font-size: 0.92rem; color: #1e2a35; font-weight: 600; }
.fi-instrucciones { display: flex; flex-direction: column; gap: 3px; }
.fi-instrucciones p { margin: 0; font-size: 0.88rem; color: #374151; line-height: 1.5; white-space: pre-line; }

.config-selector { display: flex; align-items: center; gap: 8px; }
.config-selector-label { font-size: 0.85rem; color: #5b6b7a; }
.config-select {
  padding: 4px 8px;
  border-radius: var(--radius-md);
  border: 1px solid #d9e1ea;
  font-size: 0.88rem;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
}

/* ── Estado badges ── */
.estado-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: capitalize;
}
.estado-borrador   { background: #fef9c3; color: #854d0e; }
.estado-publicada  { background: #d1fae5; color: #065f46; }
.estado-cerrada    { background: #e5e7eb; color: #374151; }
.estado-pendiente  { background: #fef9c3; color: #854d0e; }
.estado-publicado  { background: #d1fae5; color: #065f46; }
.estado-cerrado    { background: #e5e7eb; color: #374151; }
.estado-envio-pendiente { background: #fef9c3; color: #854d0e; }
.estado-envio-enviado   { background: #d1fae5; color: #065f46; }
.estado-envio-error     { background: #fee2e2; color: #991b1b; }

/* ── Tabs — sin scroll horizontal ── */
.tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  gap: 0;
  flex-wrap: nowrap;
  margin-bottom: 0;
}
.tab-btn {
  padding: 10px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  transition: color .15s, border-color .15s;
}
.tab-btn:hover { color: #0e69a0; }
.tab-btn--activo { color: #0e69a0; border-bottom-color: #0e69a0; }
.tab-badge {
  background: #0e69a0; color: #fff;
  border-radius: 999px; padding: 1px 7px;
  font-size: 0.72rem; font-weight: 700;
}

/* ── Marco / contenido ── */
.marco {
  border: 2px solid rgb(156, 155, 155);
  box-sizing: border-box;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.tab-content { display: flex; flex-direction: column; gap: 24px; }
.tab-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
}

/* ── Avisos ── */
.vacio { color: #9ca3af; font-style: italic; font-size: 0.9rem; }
.aviso-readonly {
  background: #fef9c3; border: 1px solid #fde68a;
  color: #854d0e; border-radius: 8px;
  padding: 10px 14px; font-size: 0.87rem;
}
.muted { color: #9ca3af; font-size: 0.85rem; }
.fo-vacio { padding: 28px 0 8px; }

.fo-aviso {
  background: #f0f7ff;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 18px 22px;
  margin: 0 auto 28px;
  max-width: 640px;
}
.fo-aviso-titulo {
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 700;
  color: #0c447c;
}
.fo-aviso-cuerpo {
  margin: 0 0 14px;
  font-size: 0.9rem;
  color: #1e2a35;
  line-height: 1.6;
}
.fo-aviso-pasos {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.fo-aviso-pasos li {
  font-size: 0.87rem;
  color: #374151;
  line-height: 1.5;
}

/* ── Formularios ── */
.form-card { border: 1px solid #e5e7eb; border-radius: var(--radius-md); overflow: hidden; }
.form-card-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: #f9fafb;
  border-bottom: 1px solid #e5e7eb; flex-wrap: wrap; gap: 8px;
}
.form-card-grupo { display: flex; align-items: center; gap: 10px; }
.form-card-acciones { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

.snapshot-lista { padding: 16px; display: flex; flex-direction: column; gap: 16px; }
.snapshot-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.snap-header { display: flex; align-items: flex-start; gap: 10px; }
.snap-n {
  background: #0e69a0; color: #fff; border-radius: 5px;
  min-width: 24px; height: 24px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.75rem; font-weight: 700; flex-shrink: 0; margin-top: 1px;
}
.snap-enunciado { flex: 1; font-weight: 600; font-size: 0.9rem; color: #1e2a35; line-height: 1.45; }
.snap-respuesta-area {
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 10px 14px;
  background: #f9fafb;
  min-height: 38px;
}
.snap-respuesta-placeholder { font-size: 0.78rem; color: #9ca3af; font-style: italic; }

/* ── Destinatarios ── */
.dest-tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 0;
}
.dest-tab-btn {
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  white-space: nowrap;
  transition: color .15s, border-color .15s;
}
.dest-tab-btn:hover { color: #0e69a0; }
.dest-tab-btn--activo { color: #0e69a0; border-bottom-color: #0e69a0; }

.dest-grupo { display: flex; flex-direction: column; gap: 16px; }
.dest-titulo { margin: 0; font-size: 0.95rem; color: #1e2a35; display: flex; align-items: center; gap: 8px; }
.dest-tabla { width: 100%; border-collapse: collapse; font-size: 0.87rem; }
.dest-tabla th, .dest-tabla td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: left; }
.dest-tabla th { background: #f3f4f6; font-size: 0.78rem; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; }
.dest-input-wrap { display: flex; flex-direction: column; gap: 4px; }
.dest-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.dest-label { font-size: 0.82rem; font-weight: 600; color: #374151; }
.dest-textarea {
  border: 1px solid #d9e1ea; border-radius: var(--radius-md);
  padding: 0.6rem 0.7rem; font-size: 0.9rem;
  font-family: inherit; resize: vertical;
  width: 100%; box-sizing: border-box;
  color: #1e2a35; background: #f9f9f9;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.dest-textarea:focus {
  outline: none; border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14,105,160,0.1);
}

.vigencia-row {
  display: flex; align-items: flex-end; gap: 0.9rem;
  flex-wrap: wrap; margin-top: 0.7rem;
}
.vigencia-campo {
  display: flex; flex-direction: column; gap: 0.25rem;
  font-size: 0.78rem; font-weight: 600; color: #4a5b6b;
}
.vigencia-campo input {
  border: 1px solid #d9e1ea; border-radius: var(--radius-md);
  padding: 0.35rem 0.5rem; font-size: 0.82rem;
  font-family: inherit; color: #1e2a35; background: #f9f9f9;
}
.vigencia-campo input:focus {
  outline: none; border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14,105,160,0.1);
}
.vigencia-row .cp-link {
  border: none; background: none; color: #0e69a0;
  font-size: 0.75rem; cursor: pointer; padding: 0 0 0.4rem;
  text-decoration: underline;
}
.vigencia-ayuda {
  margin: 0.4rem 0 0; font-size: 0.75rem; color: #888;
}

/* ── Respuestas ── */
.respuestas-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

/* Toggle de vista */
.vista-toggle { display: inline-flex; border: 1px solid #d9e1ea; border-radius: 8px; overflow: hidden; }
.vista-btn {
  border: none; background: #fff; cursor: pointer;
  padding: 5px 12px; font-size: 0.82rem; font-weight: 600;
  color: #5b6b7a; font-family: inherit;
}
.vista-btn:hover { background: #f0f7ff; }
.vista-btn--activo { background: #0e69a0; color: #fff; }

/* Filtro por persona */
.persona-filtro {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.persona-filtro-label { font-size: 0.85rem; font-weight: 600; color: #374151; }
.persona-filtro-select {
  padding: 5px 10px;
  border: 1px solid #d9e1ea;
  border-radius: var(--radius-md);
  font-size: 0.88rem;
  font-family: inherit;
  background: #fff;
  cursor: pointer;
  min-width: 200px;
}
.persona-filtro-select:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14,105,160,0.1);
}

/* Vista por persona */
.persona-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; background: #fafafa; }
.persona-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; }
.persona-nombre { font-size: 0.92rem; color: #1e2a35; }
.persona-email { font-size: 0.8rem; color: #6b7280; }
.persona-fecha { font-size: 0.78rem; color: #9ca3af; margin-left: auto; }
.persona-respuestas { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.persona-respuestas li { display: flex; flex-direction: column; gap: 2px; font-size: 0.87rem; }
.pr-enunciado { color: #5b6b7a; font-size: 0.8rem; }
.pr-valor { color: #1e2a35; font-weight: 600; }
.total-badge {
  background: #0e69a0; color: #fff;
  border-radius: 999px; padding: 3px 12px;
  font-size: 0.82rem; font-weight: 700;
}
/* Participación: respondientes / correos enviados. Se empuja a la derecha para
   quedar junto al botón de sincronizar. */
.participacion-badge {
  margin-left: auto;
  display: inline-flex; align-items: baseline; gap: 6px;
  background: #eaf4fb; color: #0e69a0;
  border: 1px solid #bcddf0;
  border-radius: 999px; padding: 3px 12px;
  font-size: 0.82rem; font-weight: 700; white-space: nowrap;
}
.participacion-badge small { font-weight: 600; color: #5b6b7a; font-size: 0.74rem; }
.participacion-badge--vacio { background: #f3f4f6; color: #6b7280; border-color: #e5e7eb; }
.resp-grupo { display: flex; flex-direction: column; gap: 20px; }
.resp-pregunta { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 16px; background: #fafafa; }
.resp-enunciado { margin: 0 0 8px; font-weight: 600; font-size: 0.9rem; color: #1e2a35; }
.resp-promedio { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.prom-label { font-size: 0.78rem; font-weight: 700; color: #5b6b7a; }
.prom-valor { font-size: 1.1rem; font-weight: 700; color: #0e69a0; min-width: 40px; }
.prom-bar-wrap { flex: 1; background: #e5e7eb; border-radius: 4px; height: 10px; overflow: hidden; max-width: 200px; }
.prom-bar { height: 100%; background: #0e69a0; border-radius: 4px; transition: width .3s; }
.resp-enunciado-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 8px; }
.resp-total-badge { flex-shrink: 0; font-size: 0.72rem; font-weight: 700; color: #5b6b7a; background: #e5e7eb; padding: 2px 8px; border-radius: 999px; white-space: nowrap; }

.dist-lista { list-style: none; margin: 6px 0 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
.dist-fila { display: grid; grid-template-columns: minmax(100px, 1.5fr) 3fr 42px 36px; align-items: center; gap: 8px; font-size: 0.84rem; }
.dist-clave { color: #374151; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dist-bar-wrap { background: #e5e7eb; border-radius: 4px; height: 10px; overflow: hidden; }
.dist-bar { height: 100%; background: #0e69a0; border-radius: 4px; transition: width .3s; min-width: 0; }
.dist-pct { font-weight: 700; color: #0e69a0; text-align: right; }
.dist-count { color: #9ca3af; font-size: 0.78rem; }

.resp-textos { margin: 0; padding-left: 18px; font-size: 0.87rem; }
.resp-textos li { margin-bottom: 4px; color: #374151; }
.resp-texto-autor { font-weight: 700; color: #0e69a0; }

/* Respondientes por formulario */
.resp-personas {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.resp-personas-label { font-size: 0.8rem; font-weight: 700; color: #5b6b7a; }
.resp-persona-chip {
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.chip-rol {
  background: #0e69a0;
  color: #fff;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.7rem;
  font-weight: 600;
}

.persona-rol {
  font-size: 0.76rem;
  color: #0e69a0;
  font-weight: 600;
  background: #e8f4fd;
  border-radius: 999px;
  padding: 1px 9px;
}

/* ── Calificación ── */
.cal-existente { background: #f0f7ff; border: 1px solid #bee3f8; border-radius: 10px; padding: 14px 16px; }
.cal-existente-head { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #5b6b7a; margin-bottom: 10px; }
.cal-grid { display: flex; gap: 24px; flex-wrap: wrap; }
.cal-item { display: flex; flex-direction: column; gap: 2px; }
.cal-label { font-size: 0.78rem; font-weight: 700; color: #5b6b7a; }
.cal-valor { font-size: 1.4rem; font-weight: 700; color: #0e69a0; }
.cal-valor--tenue { font-size: 0.95rem; font-weight: 600; color: #5b6b7a; }
.cal-lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.cal-item-ind {
  background: #fff;
  border: 1px solid #d9e7f3;
  border-radius: 8px;
  padding: 12px 14px;
}
.cal-ind-cab { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 10px; }
.cal-sin-nota { margin: 0; font-size: 0.82rem; color: #6b7280; font-style: italic; }
/* Semáforo de notas — mismos umbrales que ComparativoSeccion */
.cal-nota { border-radius: 6px; padding: 2px 10px; font-size: 1.3rem; }
.nivel-excelente { background: #d4edda; color: #155724; }
.nivel-alto      { background: #d1ecf1; color: #0c5460; }
.nivel-medio     { background: #fff3cd; color: #856404; }
.nivel-bajo      { background: #f8d7da; color: #721c24; }

.cal-form { margin-top: 16px; }
.input-nota { max-width: 160px; }
.cal-form h4 { margin: 0 0 14px; font-size: 0.95rem; }
.cal-aviso {
  margin: 0 0 14px;
  padding: 10px 12px;
  background: #fdf6e7;
  border-left: 3px solid #eea222;
  border-radius: 4px;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #5c4a1e;
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-group label { font-size: 0.85rem; font-weight: 600; color: #374151; }
.form-control {
  padding: 0.6rem 0.7rem; border: 1px solid #e6e5e5;
  border-radius: var(--radius-md); font-size: 0.95rem;
  font-family: inherit; resize: vertical;
  width: 100%; box-sizing: border-box;
  color: #1e2a35; background: #f9f9f9;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.form-control:focus {
  outline: none; border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14,105,160,0.1);
}

/* ── Botones ── */
.btn {
  padding: 0.5rem 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  line-height: 1.2;
  box-sizing: border-box;
  transition: all var(--transition-fast);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}
.btn-sm { padding: 0.35rem 0.75rem; font-size: 0.82rem; }
.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover:not(:disabled) { background-color: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.5; cursor: not-allowed; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover:not(:disabled) { background-color: rgb(212, 26, 26); color: white; border-color: rgb(212, 26, 26); }
.btn.volver { background-color: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.5); color: #fff; }
.btn.volver:hover:not(:disabled) { background-color: rgba(255,255,255,0.28); border-color: #fff; }

@media (max-width: 640px) {
  .form-grid { grid-template-columns: 1fr; }
  .form-card-head { flex-direction: column; align-items: flex-start; }
  .estado-row { flex-direction: column; align-items: flex-start; }
  .tabs { flex-wrap: wrap; }
}
</style>
